import React, { forwardRef, createContext, useContext } from 'react';
import { cn } from '../../utils/cn';

const RadioGroupCtx = createContext(null);

export const Radio = forwardRef(function Radio(
  { value, label, description, isDisabled, isInvalid, className, ...rest },
  ref
) {
  const group = useContext(RadioGroupCtx);
  const isChecked = group?.value !== undefined ? group.value === value : undefined;

  return (
    <label className={cn('inline-flex items-start gap-2.5 cursor-pointer select-none', isDisabled && 'opacity-50 cursor-not-allowed', className)}>
      <span className="relative shrink-0 mt-0.5">
        <input
          ref={ref}
          type="radio"
          name={group?.name}
          value={value}
          checked={isChecked}
          onChange={group?.onChange ? (e) => group.onChange(e.target.value) : rest.onChange}
          disabled={isDisabled || group?.isDisabled}
          aria-invalid={isInvalid || undefined}
          className={cn(
            'peer w-4 h-4 rounded-full appearance-none border bg-surface',
            'border-border-strong transition-colors',
            'checked:border-ink checked:bg-ink',
            'focus-visible:ring-2 focus-visible:ring-ink/30 focus-visible:ring-offset-1 focus-visible:ring-offset-bg outline-none',
            'disabled:cursor-not-allowed',
            isInvalid && 'border-danger',
          )}
          {...rest}
        />
        <span className="absolute top-1 left-1 w-2 h-2 rounded-full bg-cream opacity-0 peer-checked:opacity-100 pointer-events-none" />
      </span>
      {(label || description) && (
        <span className="leading-tight">
          {label && <span className="block text-sm text-text">{label}</span>}
          {description && <span className="block text-xs text-mute mt-0.5">{description}</span>}
        </span>
      )}
    </label>
  );
});

export function RadioGroup({ name, value, defaultValue, onChange, legend, orientation = 'vertical', isDisabled, children, className }) {
  const [internal, setInternal] = React.useState(defaultValue);
  const actual = value !== undefined ? value : internal;
  const handle = (v) => { setInternal(v); onChange?.(v); };

  return (
    <RadioGroupCtx.Provider value={{ name, value: actual, onChange: handle, isDisabled }}>
      <fieldset className={className}>
        {legend && <legend className="text-sm font-semibold text-text mb-2">{legend}</legend>}
        <div className={cn('flex gap-3', orientation === 'vertical' ? 'flex-col' : 'flex-row')}>{children}</div>
      </fieldset>
    </RadioGroupCtx.Provider>
  );
}

Radio.Group = RadioGroup;
export default Radio;
